/**
 * Viewer360 — Custom 360° product viewer
 * No dependencies. Drag, inertia, auto-spin, touch, zoom.
 */
class Viewer360 {
  constructor(container, xmlUrl) {
    this.container = container;
    this.xmlUrl = xmlUrl;
    this.images = [];
    this.loaded = [];
    this.currentIndex = 0;
    this.isDragging = false;
    this.lastX = 0;
    this.accumulated = 0;
    this.sensitivity = 5;
    this.velocity = 0;
    this.inertiaRAF = null;
    this.autoSpinRAF = null;
    this.autoSpinDone = false;
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.isPinching = false;
    this.lastPinchDist = 0;

    this._build();
    this._loadXML();
  }

  /* ─── Build UI ─────────────────────────────────────────────── */
  _build() {
    this.container.style.cssText = 'position:relative;width:100%;height:100%;background:#fff;overflow:hidden;cursor:grab;user-select:none;-webkit-user-select:none;';

    // Loader
    this.loaderEl = this._el('div', {
      style: 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;z-index:10;background:#fff;'
    });
    const spinner = this._el('div', { style: `
      width:48px;height:48px;border:3px solid #eee;border-top-color:#222;
      border-radius:50%;animation:v360spin 0.8s linear infinite;
    `});
    this.progressBar = this._el('div', { style: 'width:160px;height:3px;background:#eee;border-radius:2px;overflow:hidden;' });
    this.progressFill = this._el('div', { style: 'height:100%;width:0%;background:#222;border-radius:2px;transition:width 0.15s;' });
    this.progressText = this._el('span', { style: 'font-size:12px;color:#999;font-family:sans-serif;' }, 'Chargement…');
    this.progressBar.appendChild(this.progressFill);
    this.loaderEl.append(spinner, this.progressBar, this.progressText);
    this.container.appendChild(this.loaderEl);

    // Canvas
    this.canvas = this._el('canvas', { style: 'display:none;width:100%;height:100%;' });
    this.ctx = this.canvas.getContext('2d');
    this.container.appendChild(this.canvas);

    // Hint overlay
    this.hintEl = this._el('div', { style: `
      position:absolute;bottom:16px;left:50%;transform:translateX(-50%);
      display:flex;align-items:center;gap:8px;padding:6px 14px;
      background:rgba(0,0,0,0.45);border-radius:20px;
      color:#fff;font-size:12px;font-family:sans-serif;
      pointer-events:none;opacity:0;transition:opacity 0.4s;
    `});
    this.hintEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l-3 3 3 3"/><path d="M19 9l3 3-3 3"/><line x1="2" y1="12" x2="22" y2="12"/></svg><span>Glissez pour tourner</span>`;
    this.container.appendChild(this.hintEl);

    // Zoom buttons
    const zoomGroup = this._el('div', { style: 'position:absolute;top:12px;right:12px;display:none;flex-direction:column;gap:6px;z-index:5;' });
    this.zoomInBtn  = this._zoomBtn('+');
    this.zoomOutBtn = this._zoomBtn('−');
    this.resetBtn   = this._zoomBtn('⊙', '32px', '11px');
    zoomGroup.append(this.zoomInBtn, this.zoomOutBtn, this.resetBtn);
    this.container.appendChild(zoomGroup);
    this.zoomGroup = zoomGroup;

    this.zoomInBtn.addEventListener('click',  () => this._setZoom(this.zoom * 1.4));
    this.zoomOutBtn.addEventListener('click', () => this._setZoom(this.zoom / 1.4));
    this.resetBtn.addEventListener('click',   () => { this._setZoom(1); this.panX = 0; this.panY = 0; this._draw(); });

    // Inject keyframe animation
    if (!document.getElementById('v360-styles')) {
      const st = document.createElement('style');
      st.id = 'v360-styles';
      st.textContent = `@keyframes v360spin{to{transform:rotate(360deg)}}`;
      document.head.appendChild(st);
    }

    this._bindEvents();
  }

  _zoomBtn(label, size = '32px', fs = '16px') {
    const btn = this._el('button', { style: `
      width:${size};height:${size};border-radius:50%;border:1px solid rgba(0,0,0,0.15);
      background:rgba(255,255,255,0.9);cursor:pointer;font-size:${fs};
      display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.12);
    `}, label);
    return btn;
  }

  /* ─── Load XML ─────────────────────────────────────────────── */
  async _loadXML() {
    try {
      const res = await fetch(this.xmlUrl);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/xml');
      const nodes = doc.querySelectorAll('images > image');
      this.images = Array.from(nodes).map(n => n.getAttribute('src'));
      if (!this.images.length) throw new Error('Aucune image dans le XML');
      await this._preload();
    } catch (e) {
      this.loaderEl.innerHTML = `<span style="color:#c00;font-family:sans-serif;font-size:13px;text-align:center;padding:20px;">Impossible de charger la vue 360°.<br><small>${e.message}</small></span>`;
    }
  }

  /* ─── Preload images ───────────────────────────────────────── */
  _preload() {
    return new Promise(resolve => {
      let done = 0;
      const total = this.images.length;
      this.loaded = new Array(total).fill(null);

      this.images.forEach((src, i) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = img.onerror = () => {
          this.loaded[i] = img;
          done++;
          const pct = Math.round((done / total) * 100);
          this.progressFill.style.width = pct + '%';
          this.progressText.textContent = `${pct}%`;
          if (done === total) { this._showViewer(); resolve(); }
        };
        img.src = src;
      });
    });
  }

  /* ─── Show viewer ──────────────────────────────────────────── */
  _showViewer() {
    this.loaderEl.style.display = 'none';
    this.canvas.style.display = 'block';
    this.zoomGroup.style.display = 'flex';
    this._resize();
    this._draw();
    this._autoSpin();

    // Show hint
    setTimeout(() => { this.hintEl.style.opacity = '1'; }, 300);
    setTimeout(() => { this.hintEl.style.opacity = '0'; }, 2800);
  }

  /* ─── Auto-spin on load ────────────────────────────────────── */
  _autoSpin() {
    const total = this.images.length;
    const step = () => {
      if (this.isDragging || this.autoSpinDone) return;
      this.currentIndex = (this.currentIndex + 1) % total;
      this._draw();
      if (this.currentIndex === 0) {
        this.autoSpinDone = true;
        return;
      }
      this.autoSpinRAF = requestAnimationFrame(() => setTimeout(step, 30));
    };
    setTimeout(step, 400);
  }

  /* ─── Draw ─────────────────────────────────────────────────── */
  _draw() {
    const img = this.loaded[this.currentIndex];
    if (!img || !img.complete) return;
    const { width: cw, height: ch } = this.canvas;
    this.ctx.clearRect(0, 0, cw, ch);

    // Fit image
    const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight) * this.zoom;
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    const x = (cw - w) / 2 + this.panX;
    const y = (ch - h) / 2 + this.panY;

    this.ctx.drawImage(img, x, y, w, h);
  }

  _resize() {
    const rect = this.container.getBoundingClientRect();
    this.canvas.width  = rect.width  || this.container.offsetWidth;
    this.canvas.height = rect.height || this.container.offsetHeight;
  }

  /* ─── Events ───────────────────────────────────────────────── */
  _bindEvents() {
    const c = this.container;
    // Mouse
    c.addEventListener('mousedown',  e => this._dragStart(e.clientX, e.clientY));
    window.addEventListener('mousemove', e => this._dragMove(e.clientX, e.clientY));
    window.addEventListener('mouseup',   () => this._dragEnd());
    // Touch
    c.addEventListener('touchstart', e => {
      if (e.touches.length === 2) { this.isPinching = true; this.lastPinchDist = this._pinchDist(e); return; }
      this._dragStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    c.addEventListener('touchmove', e => {
      if (e.touches.length === 2) { this._handlePinch(e); return; }
      if (!this.isPinching) this._dragMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    c.addEventListener('touchend', () => { this.isPinching = false; this._dragEnd(); });
    // Wheel zoom
    c.addEventListener('wheel', e => { e.preventDefault(); this._setZoom(this.zoom * (e.deltaY < 0 ? 1.1 : 0.91)); }, { passive: false });
    // Double-click zoom
    c.addEventListener('dblclick', () => this.zoom > 1 ? (this.zoom=1,this.panX=0,this.panY=0,this._draw()) : this._setZoom(2));
    // Resize
    window.addEventListener('resize', () => { this._resize(); this._draw(); });
  }

  _dragStart(x, y) {
    this.isDragging = true;
    this.lastX = x;
    this.velocity = 0;
    cancelAnimationFrame(this.inertiaRAF);
    this.container.style.cursor = 'grabbing';

    // Pan when zoomed
    this._panStartX = x;
    this._panStartY = y;
    this._panOriginX = this.panX;
    this._panOriginY = this.panY;
  }

  _dragMove(x, y) {
    if (!this.isDragging) return;
    const dx = x - this.lastX;
    this.lastX = x;
    this.velocity = dx;

    if (this.zoom > 1) {
      // Pan mode
      this.panX = this._panOriginX + (x - this._panStartX);
      this.panY = this._panOriginY + (y - this._panStartY);
      this._draw();
      return;
    }

    this.accumulated += dx;
    const steps = Math.trunc(this.accumulated / this.sensitivity);
    if (steps !== 0) {
      this.accumulated -= steps * this.sensitivity;
      const total = this.images.length;
      this.currentIndex = ((this.currentIndex - steps) % total + total) % total;
      this._draw();
    }
  }

  _dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.container.style.cursor = 'grab';
    if (this.zoom <= 1) this._inertia();
  }

  _inertia() {
    if (Math.abs(this.velocity) < 0.5) return;
    this.velocity *= 0.92;
    this.accumulated += this.velocity;
    const steps = Math.trunc(this.accumulated / this.sensitivity);
    if (steps !== 0) {
      this.accumulated -= steps * this.sensitivity;
      const total = this.images.length;
      this.currentIndex = ((this.currentIndex - steps) % total + total) % total;
      this._draw();
    }
    this.inertiaRAF = requestAnimationFrame(() => this._inertia());
  }

  _handlePinch(e) {
    const dist = this._pinchDist(e);
    const delta = dist / this.lastPinchDist;
    this._setZoom(this.zoom * delta);
    this.lastPinchDist = dist;
  }

  _pinchDist(e) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  _setZoom(z) {
    this.zoom = Math.max(1, Math.min(5, z));
    if (this.zoom === 1) { this.panX = 0; this.panY = 0; }
    this._draw();
  }

  /* ─── Helpers ──────────────────────────────────────────────── */
  _el(tag, attrs = {}, text = '') {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    if (text) el.textContent = text;
    return el;
  }
}
